import { createStrip, STRIP_ENGINE_VERSION } from '@filestrip/sdk'
import type { InspectOutcome, PurgeOutcome } from '@filestrip/sdk'
import { el, node, clear, formatBytes, download } from './dom.js'
import { EXIF_GROUPS, leftoverTags, pickTag } from './fields.js'

const strip = createStrip({
  visionWorkerUrl: new URL('vision.worker.js', location.href),
  blurThreshold: 0.6
})

el('engine-version').textContent = `v${STRIP_ENGINE_VERSION.split('.').slice(0, 2).join('.')}`

const previewImg = el<HTMLImageElement>('preview-img')
const stage = el('stage')
const stageEmpty = el('stage-empty')
const unblur = el<HTMLButtonElement>('unblur')
const metaSize = el('meta-size')
const metaType = el('meta-type')
const thumbImg = el<HTMLImageElement>('thumb-img')
const thumbEmpty = el('thumb-empty')
const thumbEdit = el<HTMLButtonElement>('thumb-edit')
const thumbFile = el<HTMLInputElement>('thumb-file')
const privacy = el('privacy')
const stripStatus = el('strip-status')
const stripBtn = el<HTMLButtonElement>('strip')
const groupsEl = el('exif-groups')
const zone = el<HTMLButtonElement>('zone')
const homeZone = el('home-zone')
const homeTitle = el('home-title')
const homeLede = el('home-lede')
const zoneTitle = el('zone-title')
const zoneSub = el('zone-sub')
const pick = el<HTMLButtonElement>('pick')
const tabSanitize = el<HTMLButtonElement>('tab-sanitize')
const tabInspect = el<HTMLButtonElement>('tab-inspect')
const fileInput = el<HTMLInputElement>('file')
const log = el<HTMLUListElement>('log')
const dock = el('dock')
const dockFile = el('dock-file')
const dockUrl = el<HTMLFormElement>('dock-url')
const urlInput = el<HTMLInputElement>('url-input')
const tabFile = el<HTMLButtonElement>('tab-file')
const tabUrl = el<HTMLButtonElement>('tab-url')

type FieldState = { id: string; label: string; value: string; removed: boolean }
type GroupState = { id: string; title: string; open: boolean; fields: FieldState[] }

let busy = false
let zoom = 1
let currentFile: File | null = null
let currentReport: InspectOutcome | null = null
let groups: GroupState[] = emptyGroups()
const urls = new Set<string>()

function trackUrl(blob: Blob): string {
  const url = URL.createObjectURL(blob)
  urls.add(url)
  return url
}

function releaseUrls(): void {
  for (const url of urls) URL.revokeObjectURL(url)
  urls.clear()
}

function emptyGroups(): GroupState[] {
  return EXIF_GROUPS.map((group) => ({
    id: group.id,
    title: group.title,
    open: group.id === 'camera',
    fields: group.fields.map((field) => ({ id: field.id, label: field.label, value: '', removed: false }))
  }))
}

function setView(view: 'idle' | 'work'): void {
  document.body.dataset['view'] = view
}

function setHomeCopy(mode: 'sanitize' | 'inspect'): void {
  const sanitize = mode === 'sanitize'
  tabSanitize.classList.toggle('on', sanitize)
  tabInspect.classList.toggle('on', !sanitize)
  tabSanitize.setAttribute('aria-selected', String(sanitize))
  tabInspect.setAttribute('aria-selected', String(!sanitize))
  if (sanitize) {
    homeTitle.textContent = 'Sanitize images instantly'
    homeLede.textContent =
      'Paste or drop a photo. EXIF data is scrubbed, the canvas is cleaned, and the filename is randomized. Zero server uploads.'
  } else {
    homeTitle.textContent = 'Inspect exposure risk'
    homeLede.textContent =
      'Deep analysis before you purge. Telemetry, explicit-content rating, and identity-document detection all run off-thread on this device.'
  }
}

tabSanitize.onclick = () => setHomeCopy('sanitize')
tabInspect.onclick = () => setHomeCopy('inspect')

el('brand').onclick = (event) => {
  event.preventDefault()
  showLanding()
}

function note(message: string, detail: string, isError = false): void {
  const li = node('li')
  li.append(
    node('span', isError ? 'err' : undefined, message),
    node('span', isError ? 'meta err' : 'meta', detail)
  )
  log.prepend(li)
  while (log.children.length > 4) log.lastElementChild?.remove()
}

function imagesFrom(list: ArrayLike<File>): File[] {
  return Array.from(list).filter((file) => file.type.startsWith('image/'))
}

function applyZoom(): void {
  previewImg.style.transform = `scale(${zoom})`
}

el<HTMLButtonElement>('zoom-in').onclick = () => {
  zoom = Math.min(4, zoom + 0.25)
  applyZoom()
}
el<HTMLButtonElement>('zoom-out').onclick = () => {
  zoom = Math.max(0.5, zoom - 0.25)
  applyZoom()
}
el<HTMLButtonElement>('zoom-reset').onclick = () => {
  zoom = 1
  applyZoom()
}

function setCallout(target: HTMLElement, tone: string, title: string, copy: string): void {
  target.dataset['tone'] = tone
  const heading = target.querySelector('strong')
  const body = heading?.nextElementSibling
  if (heading) heading.textContent = title
  if (body) body.textContent = copy
}

function liveFields(): FieldState[] {
  return groups.flatMap((group) => group.fields.filter((field) => !field.removed && field.value))
}

function renderGroups(): void {
  clear(groupsEl)
  for (const group of groups) {
    const wrap = node('details', 'fold')
    wrap.open = group.open
    wrap.ontoggle = () => {
      group.open = wrap.open
    }

    const summary = node('summary')
    summary.append(node('span', undefined, group.title))
    wrap.append(summary)

    for (const field of group.fields) {
      const row = node('div', 'exif-row')
      row.dataset['id'] = field.id
      row.append(node('span', 'k', field.label))
      const value = node('span', field.value && !field.removed ? 'v' : 'v na', field.removed || !field.value ? 'N/A' : field.value)
      row.append(value)
      const actions = node('div', 'row-actions')
      const edit = node('button', 'icon-btn')
      edit.type = 'button'
      edit.title = 'Edit'
      edit.textContent = '✎'
      edit.onclick = () => editField(field)
      const drop = node('button', 'icon-btn danger')
      drop.type = 'button'
      drop.title = 'Remove'
      drop.textContent = '✕'
      drop.onclick = () => {
        field.value = ''
        field.removed = true
        renderGroups()
      }
      actions.append(edit, drop)
      row.append(actions)
      wrap.append(row)
    }
    groupsEl.append(wrap)
  }
}

function editField(field: FieldState): void {
  const next = window.prompt(`Edit ${field.label}`, field.value)
  if (next === null) return
  field.value = next.trim()
  field.removed = false
  renderGroups()
}

el<HTMLButtonElement>('expand-all').onclick = () => {
  for (const group of groups) group.open = true
  renderGroups()
}
el<HTMLButtonElement>('collapse-all').onclick = () => {
  for (const group of groups) group.open = false
  renderGroups()
}

function exportPayload(): Record<string, string> {
  const out: Record<string, string> = {}
  for (const field of liveFields()) out[field.label] = field.value
  return out
}

el<HTMLButtonElement>('export-txt').onclick = () => {
  const lines = liveFields().map((field) => `${field.label}: ${field.value}`)
  download(new Blob([lines.join('\n') || 'No EXIF data'], { type: 'text/plain' }), 'filestrip-exif.txt')
}
el<HTMLButtonElement>('export-csv').onclick = () => {
  const rows = ['Field,Value', ...liveFields().map((field) => `"${field.label.replaceAll('"', '""')}","${field.value.replaceAll('"', '""')}"`)]
  download(new Blob([rows.join('\n')], { type: 'text/csv' }), 'filestrip-exif.csv')
}
el<HTMLButtonElement>('export-json').onclick = () => {
  download(new Blob([JSON.stringify(exportPayload(), null, 2)], { type: 'application/json' }), 'filestrip-exif.json')
}

function fillGroups(tags: Record<string, string>): void {
  groups = EXIF_GROUPS.map((group) => ({
    id: group.id,
    title: group.title,
    open: group.id === 'camera' || group.id === 'gps' && Boolean(tags['Latitude']),
    fields: group.fields.map((field) => ({
      id: field.id,
      label: field.label,
      value: pickTag(tags, field.from),
      removed: false
    }))
  }))

  const extra = leftoverTags(tags)
  if (extra.length) {
    groups.push({
      id: 'other',
      title: 'Other Metadata',
      open: false,
      fields: extra.map(([key, value]) => ({ id: key, label: key, value, removed: false }))
    })
  }
  renderGroups()
}

function privacyFrom(report: InspectOutcome): void {
  const risks: string[] = []
  if (report.telemetry.gps) risks.push('GPS coordinates')
  if (report.telemetry.vectors.find((v) => v.id === 'serial')?.hits.length) risks.push('device serials')
  if (report.sentinel?.pii.length) risks.push('text PII')
  if (report.sentinel?.documents.length) risks.push('identity documents')
  if (report.sentinel?.faces.length) risks.push('faces')
  if (report.shouldBlur) risks.push('explicit content')

  if (risks.length) {
    setCallout(privacy, 'warn', 'Sensitive data detected', `This file exposes ${risks.join(', ')}. Strip before you share it.`)
    return
  }
  if (report.telemetry.exifTags || report.telemetry.trailingBytes) {
    setCallout(privacy, 'warn', 'Metadata present', 'No GPS or identity hits, but hidden tags or trailing bytes are still in the file.')
    return
  }
  setCallout(privacy, 'good', 'No sensitive data detected', 'This image appears safe to share publicly.')
}

function stripFrom(report: InspectOutcome): void {
  const count = report.telemetry.exifTags
  const trailing = report.telemetry.trailingBytes
  if (!count && !trailing) {
    setCallout(stripStatus, 'idle', 'No Metadata Found', 'This image contains no EXIF data or appended payload. There is nothing to strip.')
    stripBtn.disabled = true
    stripBtn.textContent = 'No Metadata to Strip'
    return
  }
  const bits = []
  if (count) bits.push(`${count} EXIF tag${count === 1 ? '' : 's'}`)
  if (report.telemetry.gps) bits.push('GPS')
  if (trailing) bits.push(`${formatBytes(trailing)} appended`)
  setCallout(stripStatus, 'warn', 'Metadata ready to purge', `STRIP Engine will drop ${bits.join(', ')} and randomize the filename.`)
  stripBtn.disabled = false
  stripBtn.textContent = 'Strip EXIF Data'
}

function showPreview(file: Blob, name: string, shouldBlur: boolean): void {
  previewImg.src = trackUrl(file)
  previewImg.alt = `Preview of ${name}`
  previewImg.hidden = false
  stageEmpty.hidden = true
  stage.classList.toggle('blurred', shouldBlur)
  unblur.hidden = false
  unblur.textContent = shouldBlur ? 'Unblur' : 'Blur preview'
  zoom = 1
  applyZoom()
}

function showThumbnail(jpeg: Uint8Array | null, fallback: InspectOutcome['telemetry']['ghostThumbnail']): void {
  thumbImg.hidden = true
  thumbEmpty.hidden = false
  if (jpeg && jpeg.length > 3 && jpeg[0] === 0xff && jpeg[1] === 0xd8) {
    thumbImg.src = trackUrl(new Blob([jpeg], { type: 'image/jpeg' }))
    thumbImg.hidden = false
    thumbEmpty.hidden = true
    return
  }
  if (fallback) {
    thumbEmpty.innerHTML = `<strong>Embedded preview</strong><span>${fallback.width && fallback.height ? `${fallback.width} × ${fallback.height}` : 'IFD1'} · ${formatBytes(fallback.bytes)}</span>`
  } else {
    thumbEmpty.innerHTML = `<strong>No embedded thumbnail</strong><span>Click Edit to preview a custom thumbnail</span>`
  }
}

function showLanding(): void {
  busy = false
  releaseUrls()
  renderIdle()
  setView('idle')
  pick.disabled = false
  zoneTitle.textContent = 'Drag and drop files'
  zoneSub.textContent = 'or paste with Ctrl+V'
}

function renderIdle(): void {
  currentFile = null
  currentReport = null
  groups = emptyGroups()
  renderGroups()
  previewImg.hidden = true
  previewImg.removeAttribute('src')
  stageEmpty.hidden = false
  stage.classList.remove('blurred')
  unblur.hidden = true
  metaSize.textContent = '—'
  metaType.textContent = '—'
  showThumbnail(null, null)
  setCallout(privacy, 'idle', 'Waiting for a file', 'Inspect and strip run together on this device. Nothing is uploaded.')
  setCallout(stripStatus, 'idle', 'No file loaded', 'Drop a photo to see what the STRIP Engine will purge.')
  stripBtn.disabled = true
  stripBtn.textContent = 'No Metadata to Strip'
}

async function loadFile(file: File): Promise<void> {
  if (busy) return
  busy = true
  setView('work')
  pick.disabled = true
  zoneTitle.textContent = 'Working…'
  zoneSub.textContent = 'Everything stays on this device'
  releaseUrls()
  currentFile = file
  metaSize.textContent = formatBytes(file.size)
  metaType.textContent = file.type || 'image'
  showPreview(file, file.name, false)
  unblur.hidden = true
  setCallout(privacy, 'idle', 'Analyzing…', 'Telemetry, vision, and PII scan stay on this device.')
  setCallout(stripStatus, 'idle', 'Reading metadata…', 'EXIF, GPS, serials, and trailing bytes.')
  stripBtn.disabled = true

  try {
    const report = await strip.inspect(file)
    currentReport = report
    fillGroups(report.telemetry.tags)
    showPreview(file, file.name, report.shouldBlur)
    showThumbnail(report.telemetry.thumbnailJpeg, report.telemetry.ghostThumbnail)
    privacyFrom(report)
    stripFrom(report)
  } catch (err) {
    note(file.name, err instanceof Error ? err.message : 'inspection failed', true)
    showLanding()
  } finally {
    busy = false
    pick.disabled = false
    zoneTitle.textContent = 'Drag and drop files'
    zoneSub.textContent = 'or paste with Ctrl+V'
  }
}

unblur.onclick = () => {
  const blurred = stage.classList.toggle('blurred')
  unblur.textContent = blurred ? 'Unblur' : 'Blur preview'
}

thumbEdit.onclick = () => thumbFile.click()
thumbFile.onchange = () => {
  const file = thumbFile.files?.[0]
  thumbFile.value = ''
  if (!file) return
  thumbImg.src = trackUrl(file)
  thumbImg.hidden = false
  thumbEmpty.hidden = true
}

stripBtn.onclick = async () => {
  if (!currentFile || busy) return
  busy = true
  stripBtn.disabled = true
  stripBtn.textContent = 'Purging…'
  try {
    const result: PurgeOutcome = await strip.sanitize(currentFile)
    download(result.blob, result.name)
    note(result.name, result.headline)
    const cleaned = new File([result.blob], result.name, { type: result.blob.type })
    busy = false
    await loadFile(cleaned)
  } catch (err) {
    note('Purge failed', err instanceof Error ? err.message : 'unknown error', true)
    stripBtn.textContent = 'Strip EXIF Data'
    stripBtn.disabled = false
    busy = false
  }
}

function accept(list: ArrayLike<File>): void {
  const images = imagesFrom(list)
  if (!images.length) {
    note('No images found', 'drop a JPEG, PNG, or WebP', true)
    return
  }
  void loadFile(images[0]!)
  for (const extra of images.slice(1)) {
    void strip.sanitize(extra).then((result) => {
      download(result.blob, result.name)
      note(result.name, result.headline)
    }).catch((err: unknown) => {
      note(extra.name, err instanceof Error ? err.message : 'purge failed', true)
    })
  }
}

tabFile.onclick = () => {
  tabFile.classList.add('on')
  tabUrl.classList.remove('on')
  dockFile.hidden = false
  dockUrl.hidden = true
}
tabUrl.onclick = () => {
  tabUrl.classList.add('on')
  tabFile.classList.remove('on')
  dockFile.hidden = true
  dockUrl.hidden = false
  urlInput.focus()
}

zone.onclick = () => {
  if (!busy) fileInput.click()
}
homeZone.onclick = (event) => {
  if ((event.target as HTMLElement).closest('#pick')) return
  if (!busy) fileInput.click()
}
pick.onclick = (event) => {
  event.stopPropagation()
  if (!busy) fileInput.click()
}
fileInput.onchange = () => {
  if (fileInput.files?.length) accept(fileInput.files)
  fileInput.value = ''
}

dockUrl.onsubmit = async (event) => {
  event.preventDefault()
  const href = urlInput.value.trim()
  if (!href) return
  try {
    const response = await fetch(href)
    if (!response.ok) throw new Error(`${response.status}`)
    const blob = await response.blob()
    if (!blob.type.startsWith('image/')) throw new Error('URL was not an image')
    const name = href.split('/').pop()?.split('?')[0] || 'remote.jpg'
    await loadFile(new File([blob], name, { type: blob.type }))
  } catch (err) {
    note('Could not load URL', err instanceof Error ? err.message : 'fetch failed', true)
  }
}

let dragDepth = 0
function clearHot(): void {
  dock.classList.remove('hot')
  zone.classList.remove('hot')
  homeZone.classList.remove('hot')
}

document.addEventListener('dragenter', (event) => {
  event.preventDefault()
  dragDepth++
  if (document.body.dataset['view'] === 'work') {
    dock.classList.add('hot')
    zone.classList.add('hot')
  } else {
    homeZone.classList.add('hot')
  }
})
document.addEventListener('dragover', (event) => event.preventDefault())
document.addEventListener('dragleave', (event) => {
  event.preventDefault()
  dragDepth--
  if (dragDepth <= 0) {
    dragDepth = 0
    clearHot()
  }
})
document.addEventListener('drop', (event) => {
  event.preventDefault()
  dragDepth = 0
  clearHot()
  const files = event.dataTransfer?.files
  if (files?.length) accept(files)
})

window.addEventListener('paste', (event) => {
  if (busy || !event.clipboardData) return
  const found: File[] = []
  for (const item of event.clipboardData.items) {
    if (!item.type.startsWith('image/')) continue
    const file = item.getAsFile()
    if (file) found.push(file)
  }
  if (found.length) {
    event.preventDefault()
    accept(found)
  }
})

window.addEventListener('beforeunload', () => {
  releaseUrls()
  strip.dispose()
})

showLanding()
